import { AuthShellTone, AuthStatusView } from '@today/ui-domain'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'
import { asKnownNoAccessReason, noAccessMessageKey } from '@/auth/auth-errors'
import { useAuthLogo } from '@/auth/useAuthLogo'
import { AUTH_NAMESPACE } from '@/i18n'

/**
 * Shown when the callback is refused because the signed-in identity has no
 * usable TODAY account — none, an ambiguous one, or an inactive one
 * (TO-361 login-surface REQ-006). One user belongs to exactly one tenant
 * (ADR-0032), so there is never a tenant choice here.
 *
 * The incoming state is untrusted, so the reason is narrowed to a known
 * no-access code before it is rendered; anything else is ignored. An MFA
 * refusal (TO-63 REQ-014) shows its own message instead of the generic one.
 */
export function NoAccessPage() {
	const { state } = useLocation() as { state: { reason?: unknown } | null }
	const reason = asKnownNoAccessReason(state?.reason)
	const { t } = useTranslation(AUTH_NAMESPACE)
	const logo = useAuthLogo()
	// Message keys are `<namespace>.<key path>` (e.g. `auth.errors.mfaRequired`).
	const [namespace, ...keyPath] = noAccessMessageKey(reason).split('.')

	return (
		<AuthStatusView
			logo={logo}
			tone={AuthShellTone.Warning}
			title={t('noAccess.title')}
			message={t(keyPath.join('.'), { ns: namespace })}
			reason={reason ? { label: t('status.reasonLabel'), code: reason } : null}
			action={{ label: t('status.backToLogin'), href: '/login' }}
		/>
	)
}
